/**
 * printLexical — 인쇄 경로 공용 Lexical 변환 + 풀이 데이터 파이프라인.
 *
 * 출처 통합(2026-09-01, 오답노트 v4 이관):
 *  - lexicalToPrintHtml: ErrorNotePrint 내부 convertLexicalToHtml 를 순수 함수로 추출.
 *    printPreview 풀이 변환과 동일 규칙(equation→processMathEquation, box-content→클래스 부여).
 *    입력을 깊은 복사해 원본을 오염시키지 않는다(종전 ErrorNotePrint 는 제자리 변형이었다).
 *  - fetchPrintSolutions: printPreview.js 의 풀이 fetch 효과 본문을 추출.
 *    시험지(v4)와 오답노트가 같은 계약(/api/solutions/list → 상세 lexical)을 쓰기 위한 단일 사본.
 *
 * fetchJson 계약: (url:string)=>Promise<parsedJson> — 성공 시 파싱된 본문, 실패 시 throw.
 * (printPreview 의 fetchWithTokenRefresh, 오답노트 어댑터의 fetchJson 이 이 계약을 만족한다)
 */
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation } from './latexParser';
import {
  SOLUTION_DETAIL_CONCURRENCY,
  SOLUTION_LIST_BATCH,
  SOLUTION_STOP_AFTER_FAILURES,
  chunkIds,
  mapWithLimit,
} from '../../print/printRequestLimits';

const processNode = (node) => {
  if (node.type === 'equation') {
    node.equation = processMathEquation(node.equation);
  } else if (node.type === 'box-content') {
    node.className = 'PlaygroundEditorTheme__boxContent';
  }
  if (node.children && Array.isArray(node.children)) {
    node.children = node.children.map((child) => processNode(child));
  }
  return node;
};

/** Lexical JSON → 인쇄용 HTML. 실패·빈 입력은 '' — 호출부가 null 처리로 바꿔 쓴다. */
export function lexicalToPrintHtml(lexicalData) {
  if (!lexicalData || !lexicalData.root) return '';
  try {
    const editor = createEditor({
      nodes: [...PlaygroundNodes],
      onError: (error) => {
        console.error('Lexical Error:', error);
      },
    });
    const data = JSON.parse(JSON.stringify(lexicalData));
    data.root = processNode(data.root);
    const editorState = editor.parseEditorState(data);
    editor.setEditorState(editorState);
    let content = '';
    editor.update(() => {
      content = $generateHtmlFromNodes(editor);
    });
    return content;
  } catch (error) {
    console.error('Lexical 처리 중 오류:', error);
    return '';
  }
}

/**
 * 문제 목록의 풀이를 조회해 v4 solutions 계약([{problem_id, number, content, answer, failed?}])으로 만든다.
 * 정본 선택(최소 solution_id)·status>=999 게이트는 printPreview 종전 동작 그대로.
 *
 * - 목록은 서버 제한(SOLUTION_LIST_BATCH)만큼 나눠 차례로 묻는다. 그 이하면 종전과 같은 요청 한 번이다.
 * - 상세는 동시에 SOLUTION_DETAIL_CONCURRENCY 개까지만 보낸다.
 * - 항목별 실패는 throw 하지 않고 failed: true 로 돌려준다. 「풀이 없음」(content null)과 구분하기 위해서다.
 * - 요청이 연달아 실패하면 남은 요청을 보내지 않고 실패로 센다. signal 이 중단돼도 더 보내지 않는다.
 * - previous 에 실패 아닌 항목이 있으면 그 문제는 다시 묻지 않는다([다시 시도]가 실패한 것만 묻게).
 * - 멈춘 탓에 묻지 못한 문제는 untried 로 표시하고, 다시 시도 때 먼저 묻는다. 늘 실패하는 문제가
 *   앞에 몰려 있어도 뒤 풀이를 받기 위해서다(같은 순서로 물으면 매번 같은 자리에서 멈춘다).
 */
export async function fetchPrintSolutions(problems, fetchJson, { previous = [], signal } = {}) {
  const kept = new Map();
  const untried = new Set();
  previous.forEach((sol) => {
    if (!sol.failed) kept.set(String(sol.problem_id), sol);
    else if (sol.untried) untried.add(String(sol.problem_id));
  });

  let failuresInARow = 0;
  const canRequest = () => !signal?.aborted && failuresInARow < SOLUTION_STOP_AFTER_FAILURES;
  const request = async (url) => {
    try {
      const response = await fetchJson(url);
      failuresInARow = 0;
      return response;
    } catch (error) {
      failuresInARow += 1;
      throw error;
    }
  };

  const pendingIds = problems
    .map((problem) => problem.problem_id)
    .filter((id) => !kept.has(String(id)));
  const solutionsMap = {};
  const listFailed = new Set();
  for (const ids of chunkIds(pendingIds, SOLUTION_LIST_BATCH)) {
    try {
      if (!canRequest()) throw new Error('풀이 요청 중단');
      // 한 묶음씩 끝내고 다음을 묻는다 — 서버에 한꺼번에 몰리지 않게
      const listResponse = await request(`/api/solutions/list?problem_ids=${ids.join(',')}`);
      if (!Array.isArray(listResponse?.data)) throw new Error('풀이 목록 응답 형식 오류');
      listResponse.data.forEach((item) => {
        solutionsMap[item.problem_id] = item.solutions || [];
      });
    } catch (error) {
      // 창을 닫아 멈춘 것은 실패가 아니다 — 콘솔에 실패로 남기지 않는다
      if (!signal?.aborted) console.error(`풀이 목록 조회 실패 (${ids.length}문제):`, error);
      ids.forEach((id) => listFailed.add(String(id)));
    }
  }

  const results = new Array(problems.length);
  const askOrder = problems
    .map((problem, idx) => ({ problem, idx }))
    .sort((a, b) => Number(untried.has(String(b.problem.problem_id))) - Number(untried.has(String(a.problem.problem_id))));

  const solutionOf = async (problem, idx) => {
    const id = String(problem.problem_id);
    if (kept.has(id)) return { ...kept.get(id), number: idx + 1 };

    const base = {
      problem_id: problem.problem_id,
      number: idx + 1,
      content: null,
      answer: problem.answer || '',
    };
    if (listFailed.has(id)) return { ...base, failed: true };

    const solList = solutionsMap[problem.problem_id] || [];
    const sol = solList.length > 0
      ? solList.reduce((prev, curr) => (prev.solution_id < curr.solution_id ? prev : curr))
      : null;
    if (!sol || !(sol.solution_info?.status >= 999)) return base;
    if (!canRequest()) return { ...base, failed: true, untried: true };

    try {
      const detailResponse = await request(`/api/solutions/${sol.solution_id}?lexical=true`);
      const detailData = detailResponse?.data;
      if (detailData?.lexical_data?.lexical_data) {
        return { ...base, content: lexicalToPrintHtml(detailData.lexical_data.lexical_data) || null };
      }
      return base;
    } catch (error) {
      console.error(`풀이 조회 오류 (problem_id: ${problem.problem_id}):`, error);
      return { ...base, failed: true };
    }
  };

  await mapWithLimit(askOrder, SOLUTION_DETAIL_CONCURRENCY, async ({ problem, idx }) => {
    results[idx] = await solutionOf(problem, idx);
  });
  return results;
}
